import type { ReactNode } from 'react';
import { Children, useEffect, useId, useRef, useState } from 'react';

import classNames from 'classnames';
import fastdom from 'fastdom';

import styles from './Carousel.module.css';

import { ArrowButton } from '../ArrowButton';
import { ScrollSnap } from '../ScrollSnap';
import { CarouselItem } from './CarouselItem';
import { isOverflowingLeft } from './isOverflowingLeft';
import { isOverflowingRight } from './isOverflowingRight';

interface HTMLElementWithChildren extends HTMLElement {
  children: HTMLCollectionOf<HTMLElement>;
  lastChild: HTMLElement;
  firstChild: HTMLElement;
  nextElementSibling: HTMLElement;
}

export interface CarouselChildProps {
  onMouseUp(ref: HTMLLIElement): void;
  fullWidth?: boolean;
}

export interface CarouselProps {
  /** A collection of equal width rich elements */
  children: ReactNode | ((props: CarouselChildProps) => ReactNode);
  /** Toggle next/previous buttons */
  withButtons?: boolean;
  /** Toggle margins to center first and last items when in view */
  centerFirstAndLast?: boolean;
  /** Toggle looping from last to first and first to last */
  loop?: boolean;
  /** Toggle behavior to only show one child at a time */
  asSlides?: boolean;
  className?: string;
}

/**
 * A horizontal scrolling carousel to display a collection of rich elements.
 *
 * @remarks
 * This component is not working properly. Follow
 * {@link https://app.shortcut.com/farmersdog/story/87056 | SC-87056} for more
 * details.
 * @beta
 * @see https://corgi-x.tfd.engineering/components/carousel
 */
export function Carousel({
  children,
  withButtons = false,
  centerFirstAndLast = false,
  loop = false,
  asSlides = false,
  className,
}: CarouselProps) {
  const scrollSnap = useRef<HTMLElementWithChildren>(null);
  const [blankWidth, setBlankWidth] = useState('');
  const id = useId();

  const handlePreviousClick = (): void => {
    let nextChild: HTMLElement | null | undefined;
    fastdom.measure(() => {
      if (!scrollSnap.current) {
        return;
      }

      if (loop && scrollSnap.current.scrollLeft === 0) {
        nextChild = centerFirstAndLast
          ? scrollSnap.current.children.item(
              scrollSnap.current.children.length - 2
            )
          : scrollSnap.current.lastChild;
      } else if (scrollSnap.current) {
        nextChild = Array.from(scrollSnap.current.children)
          .reverse()
          .find(child => isOverflowingLeft(child, scrollSnap.current));

        if (!nextChild) {
          nextChild = centerFirstAndLast
            ? scrollSnap.current.children.item(1)
            : scrollSnap.current.firstChild;
        }
      }
    });

    fastdom.mutate(() => {
      if (nextChild) {
        nextChild.scrollIntoView({
          inline: 'center',
          block: 'nearest',
          behavior: 'smooth',
        });
      }
    });
  };

  const handleNextClick = (): void => {
    let nextChild: HTMLElement | null | undefined;
    fastdom.measure(() => {
      if (!scrollSnap.current) {
        return;
      }
      const { scrollWidth, clientWidth } = scrollSnap.current;
      const maxScrollWidth = scrollWidth - clientWidth;
      if (loop && scrollSnap.current.scrollLeft === maxScrollWidth) {
        nextChild = centerFirstAndLast
          ? scrollSnap.current.children.item(1)
          : scrollSnap.current.firstChild;
      } else {
        nextChild = Array.from(scrollSnap.current.children).find(child =>
          isOverflowingRight(child, scrollSnap.current)
        );

        if (!nextChild) {
          nextChild = centerFirstAndLast
            ? scrollSnap.current.children.item(
                scrollSnap.current.children.length - 2
              )
            : scrollSnap.current.lastChild;
        }
      }
    });

    fastdom.mutate(() => {
      if (nextChild) {
        nextChild.scrollIntoView({
          inline: 'center',
          block: 'nearest',
          behavior: 'smooth',
        });
      }
    });
  };

  const handleChildMouseUp = (element: HTMLLIElement): void => {
    let isOverflowing: boolean;

    fastdom.measure(() => {
      isOverflowing =
        isOverflowingRight(element, scrollSnap.current) ||
        isOverflowingLeft(element, scrollSnap.current);
    });

    fastdom.mutate(() => {
      if (!isOverflowing) {
        return;
      }
      element.scrollIntoView({
        inline: 'center',
        block: 'nearest',
        behavior: 'smooth',
      });
    });
  };

  useEffect(() => {
    if (!scrollSnap.current) {
      return;
    }
    const firstRealChild = scrollSnap.current.children.item(1);

    if (!firstRealChild) {
      return;
    }

    const childWidth = firstRealChild.getBoundingClientRect().width;
    setBlankWidth(`calc(50% - ${childWidth / 2}px)`);
  }, []);

  const childProps = {
    onMouseUp: handleChildMouseUp,
    fullWidth: asSlides,
  };

  return (
    <div className={classNames(styles.container, className)}>
      {withButtons && (
        <ArrowButton
          onClick={handlePreviousClick}
          direction="left"
          aria-label="previous"
          aria-controls={id}
          className={styles.previous}
        />
      )}
      <ScrollSnap
        as="ul"
        id={id}
        containerRef={scrollSnap}
        className={styles.horizontalSnap}
      >
        {centerFirstAndLast && (
          <li className={styles.blank} style={{ marginRight: blankWidth }} />
        )}
        {typeof children === 'function'
          ? children(childProps)
          : Children.map(children, child => (
              <CarouselItem {...childProps}>{child}</CarouselItem>
            ))}
        {centerFirstAndLast && (
          <li className={styles.blank} style={{ marginLeft: blankWidth }} />
        )}
      </ScrollSnap>
      {withButtons && (
        <ArrowButton
          onClick={handleNextClick}
          direction="right"
          aria-label="next"
          aria-controls={id}
          className={styles.next}
        />
      )}
    </div>
  );
}
